import { setCacheMeta, deleteCacheMeta, touchCacheMeta } from './indexed-db.js';
import { logInfo, logError } from '../state/log-store.js';
import { mediaCacheRequest, stableMediaCacheKey, isMediaAssetUrl } from './media-cache-key.js';

const CACHE_NAME = 'media-v2';

/** @type {Map<string, string>} stableKey -> blob object URL */
const blobUrlByKey = new Map();

async function getCache() {
  return caches.open(CACHE_NAME);
}

/**
 * @param {string} url
 */
async function matchCachedResponse(url) {
  const cache = await getCache();
  const req = mediaCacheRequest(url);
  let res = await cache.match(req);
  if (res) return res;
  try {
    res = await cache.match(url, { ignoreSearch: true });
  } catch {
    /* ignoreSearch unsupported in very old browsers */
  }
  return res ?? null;
}

/**
 * @param {string} url
 * @param {Response} res
 */
async function putCachedResponse(url, res) {
  const cache = await getCache();
  const req = mediaCacheRequest(url);
  await cache.put(req, res.clone());
  try {
    await cache.put(url, res.clone());
  } catch {
    /* optional duplicate */
  }
}

export async function isCached(url) {
  const res = await matchCachedResponse(url);
  return !!res;
}

export async function fetchAndCache(url) {
  if (!isMediaAssetUrl(url)) {
    throw new Error('Not a media asset URL');
  }
  const existing = await matchCachedResponse(url);
  if (existing) {
    await setCacheMeta(
      stableMediaCacheKey(url),
      Number(existing.headers.get('content-length')) || 0,
    );
    return existing;
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Fetch failed ${res.status}`);
  await putCachedResponse(url, res);
  const sizeBytes = Number(res.headers.get('content-length')) || 0;
  await setCacheMeta(stableMediaCacheKey(url), sizeBytes);
  logInfo('Media cached', { key: stableMediaCacheKey(url), sizeBytes });
  return res;
}

/**
 * Playback URL: blob from Cache API when available (works offline, ignores presign rotation).
 * @param {string} url
 */
export async function resolvePlaybackUrl(url) {
  if (!url) return url;
  const key = stableMediaCacheKey(url);

  const existingBlob = blobUrlByKey.get(key);
  if (existingBlob) {
    await touchCacheMeta(key).catch(() => {});
    return existingBlob;
  }

  const cached = await matchCachedResponse(url);
  if (cached) {
    const blob = await cached.blob();
    const blobUrl = URL.createObjectURL(blob);
    blobUrlByKey.set(key, blobUrl);
    await touchCacheMeta(key).catch(() => {});
    logInfo('Playback from cache', { key, offline: !navigator.onLine });
    return blobUrl;
  }

  if (!navigator.onLine) {
    logError('Media not cached and offline', { key });
    return url;
  }

  return url;
}

/** Revoke blob URLs created for playback (call before replacing slot DOM). */
export function revokePlaybackBlobUrls() {
  for (const blobUrl of blobUrlByKey.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  blobUrlByKey.clear();
}

export async function removeFromCache(url) {
  const key = stableMediaCacheKey(url);
  const cache = await getCache();
  await cache.delete(mediaCacheRequest(url));
  try {
    await cache.delete(url, { ignoreSearch: true });
  } catch {
    /* ignore */
  }
  const blob = blobUrlByKey.get(key);
  if (blob) {
    URL.revokeObjectURL(blob);
    blobUrlByKey.delete(key);
  }
  await deleteCacheMeta(key);
}

export async function clearMediaCache() {
  revokePlaybackBlobUrls();
  await caches.delete(CACHE_NAME);
  logInfo('Media cache cleared');
}

export async function estimateUsage() {
  if (!navigator.storage?.estimate) {
    return { usage: 0, quota: 0 };
  }
  return navigator.storage.estimate();
}
